'use client'

import React, {useEffect, useRef, useState} from "react";
import {FlexField} from '../../FlexField'
import {SelectFieldOptionsType, SelectFieldPropsType} from '../types';
import {getStringValue} from '../lib';
import {ChevronDownSVG} from "../assets";
import {SelectFieldStyle} from "../styles";
import SelectModalWindow from "./SelectModalWindow";

export default function SelectField({
                                        value,
                                        onChange,
                                        options = [],
                                        placeholder,
                                        isError,
                                        isSearching = false,
                                        required = false,
                                        multiple = false,
                                        disabled = false,
                                        offOptionsSyncing = false,
                                        autoClose = true,
                                        onBlur,
                                        loadMoreOptions,
                                        className = SelectFieldStyle,
                                    }: SelectFieldPropsType): React.JSX.Element {
    const [isFocus, setIsFocus] = useState<boolean>(false)
    const [searchingString, setSearchingString] = useState<string>('')
    const [isShowModalWindow, setIsShowModalWindow] = useState<boolean>(false)

    const [optionsState, setOptionsState] = useState<SelectFieldOptionsType>(options)

    const inputRef = useRef<HTMLDivElement>(null)
    const modalWindowRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!offOptionsSyncing)
        setOptionsState(options)
    }, [options]);

    useEffect(() => {
        if (required && optionsState[0] && (value === undefined || Array.isArray(value) && value.length === 0)) {
            onChange && onChange(optionsState[0].value)
        }

        const onClickListener = (e: MouseEvent) => {
            const isInputElement = e.target === inputRef.current || inputRef.current?.contains(e.target as Node)
            const isModalWindowElement = e.target === modalWindowRef.current || modalWindowRef.current?.contains(e.target as Node)

            if (!isInputElement && !isModalWindowElement) {
                setIsShowModalWindow(false)
                onBlur && onBlur()
            }
        }

        window.addEventListener('click', onClickListener)
        return () => {
            window.removeEventListener('click', onClickListener)
        }
    }, []);

    const handleChange = (data: any[]) => {
        if (data.length === 0 && required) {
            let newValue

            if (Array.isArray(value)) {
                newValue = value[0]
            } else {
                newValue = value
            }

            onChange && onChange([newValue || optionsState[0]])
        } else {
            if (data.length > 1 && !multiple) {
                onChange && onChange([data[data.length - 1]])
                if (autoClose) {
                    setIsShowModalWindow(false)
                    onBlur && onBlur()
                }
            } else {
                onChange && onChange(data)
            }
        }
    }

    const getViewField = () => {
        if (isSearching) {
            return <FlexField value={isFocus ? searchingString : getStringValue(value, optionsState)}
                              onChange={setSearchingString}
                              disabled={disabled}
                              onFocus={() => {
                                  setIsFocus(true)
                                  setIsShowModalWindow(true)
                              }}
                              onBlur={() => {
                                  setIsFocus(false)
                                  setIsShowModalWindow(false)
                                  onBlur && onBlur()
                              }}
                              onClick={() => setIsShowModalWindow(true)}
                              isError={isError}
                              placeholder={placeholder}
                              dropdownIcon={<ChevronDownSVG/>}
                              ref={inputRef}
            />
        }

        return <FlexField value={getStringValue(value, optionsState)}
                          isError={isError}
                          disabled={disabled}
                          placeholder={placeholder}
                          notInput={true}
                          onClick={() => setIsShowModalWindow(true)}
                          dropdownIcon={<ChevronDownSVG/>}
                          ref={inputRef}
        />
    }

    const onScrollToBottom = async () => {
        if (loadMoreOptions) {
            const newOptions = await loadMoreOptions(optionsState?.length)
            setOptionsState(prevState => [...prevState, ...newOptions])
        }
    }

    return (
        <div className={className?.selectField}>
            {getViewField()}
            {isShowModalWindow && (
                <div ref={modalWindowRef} className={className?.modalWindow}>
                    <SelectModalWindow value={value}
                                       options={optionsState}
                                       onChange={handleChange}
                                       onScrollToBottom={onScrollToBottom}
                                       className={className}/>
                </div>
            )}
        </div>
    )
}